import { createContext, useContext, useEffect, useState } from 'react';
import type { ReclassDomainMinimum } from '../Reclassification/reclassCriteria';
import {
  fetchDistrictExitCriteria,
  type DistrictExitCriteria,
  type EffectiveGradeBand,
  type EffectiveRuleSet,
} from '../../core/exitCriteriaApi';
import { latestAdministration, type PlanCriteria, type PlanStudent } from './derive';

export const NO_RECLASS_CRITERIA: PlanCriteria = {
  state: 'your state',
  assessment: 'WIDA ACCESS',
  numeric: false,
  compositeMin: null,
  domainMinimum: null,
  domainMinimums: [],
  overallLabel: 'No exit criteria on file',
};

const Ctx = createContext<PlanCriteria>(NO_RECLASS_CRITERIA);

export const ReclassCriteriaProvider = Ctx.Provider;

export function useReclassCriteria(): PlanCriteria {
  return useContext(Ctx);
}

const MIDDLE_GRADE = '5';
const KINDERGARTEN = new Set(['K', 'KG', 'KINDERGARTEN', '0', '00']);
const DOMAIN_LABELS: Record<string, string> = {
  listening: 'Listening',
  speaking: 'Speaking',
  reading: 'Reading',
  writing: 'Writing',
};

export function gradeKey(label: string | null | undefined): string | null {
  const raw = (label ?? '').trim().replace(/^grade\s+/i, '').toUpperCase();
  if (raw === '') return null;
  if (KINDERGARTEN.has(raw)) return 'K';
  if (/^\d{1,2}$/.test(raw)) {
    const n = Number(raw);
    return n >= 1 && n <= 12 ? String(n) : null;
  }
  return null;
}

function covers(band: EffectiveGradeBand, grade: string): boolean {
  return band.grades === 'all' || band.grades.includes(grade);
}

function bandFor(bands: EffectiveGradeBand[], grade: string | null): EffectiveGradeBand | null {
  const access = bands.filter((b) => b.assessment === 'access');
  const byGrade = grade == null ? undefined : access.find((b) => covers(b, grade));
  return byGrade ?? access.find((b) => covers(b, MIDDLE_GRADE)) ?? access[0] ?? null;
}

function ruleFor(band: EffectiveGradeBand): EffectiveRuleSet | null {
  return (
    band.ruleSets.find((r) => r.outcome === 'exits') ??
    band.ruleSets.find((r) => r.outcome === 'eligible_for_review') ??
    null
  );
}

export function assessmentGrade(student: PlanStudent | null): string | null {
  if (!student) return null;
  return gradeKey(latestAdministration(student)?.grade) ?? gradeKey(student.latestGrade) ?? gradeKey(student.sis?.grade);
}

function reviewBeside(band: EffectiveGradeBand, rule: EffectiveRuleSet): Pick<PlanCriteria, 'review'> {
  if (rule.outcome !== 'exits') return {};
  const review = band.ruleSets.find((r) => r.outcome === 'eligible_for_review');
  if (!review) return {};
  return { review: { conditions: review.conditions.map((c) => ({ measure: c.measure, minimum: c.minimum })), ...(review.displayNote ? { displayNote: review.displayNote } : {}) } };
}

export function criteriaForStudent(criteria: DistrictExitCriteria | null, student: PlanStudent | null): PlanCriteria {
  return criteriaForGrade(criteria, assessmentGrade(student));
}

export function criteriaForGrade(criteria: DistrictExitCriteria | null, grade: string | null): PlanCriteria {
  if (!criteria) return NO_RECLASS_CRITERIA;
  const none: PlanCriteria = { ...NO_RECLASS_CRITERIA, state: criteria.stateName };
  if (!criteria.effective) return none;
  const band = bandFor(criteria.effective.gradeBands, grade);
  if (!band || band.noExit) {
    return band?.noExit ? { ...none, overallLabel: 'Cannot exit on ACCESS' } : none;
  }
  const rule = ruleFor(band);
  if (!rule) return none;
  const overall = rule.conditions.find((c) => c.measure === 'overall');
  const domainMinimums: ReclassDomainMinimum[] = rule.conditions.flatMap((c) => {
    const label = DOMAIN_LABELS[c.measure];
    return label ? [{ domain: label, value: c.minimum }] : [];
  });
  const compositeMin = overall ? overall.minimum : null;
  return {
    state: criteria.stateName,
    assessment: 'WIDA ACCESS',
    numeric: compositeMin != null || domainMinimums.length > 0,
    compositeMin,
    domainMinimum: domainMinimums[0] ?? null,
    domainMinimums,
    overallLabel: compositeMin != null ? compositeMin.toFixed(1) : 'No composite minimum',
    outcome: rule.outcome,
    conditions: rule.conditions.map((c) => ({ measure: c.measure, minimum: c.minimum })),
    ...reviewBeside(band, rule),
    ...(rule.displayNote ? { displayNote: rule.displayNote } : {}),
  };
}

export type DistrictExitCriteriaState = { criteria: DistrictExitCriteria | null; settled: boolean };

export function useDistrictExitCriteria(): DistrictExitCriteriaState {
  const [state, setState] = useState<DistrictExitCriteriaState>({ criteria: null, settled: false });
  useEffect(() => {
    let active = true;
    fetchDistrictExitCriteria()
      .then((criteria) => {
        if (active) setState({ criteria, settled: true });
      })
      .catch((err: unknown) => {
        console.warn('district exit criteria unavailable; plans show no exit criteria', err);
        if (active) setState({ criteria: null, settled: true });
      });
    return () => {
      active = false;
    };
  }, []);
  return state;
}
